<template>
  <div ref="wrap" class="relative ml-auto flex items-center sm:ml-10">
    <button
      class="flex items-center gap-x-2 whitespace-nowrap text-sm font-bold uppercase tracking-widest text-blue"
      @click="toggleOpenClose"
    >
      <span>
        <span>{{ $t('sort_by') }}</span>
      </span>

      <UiIcon :icon="isOpen ? 'chevronUp' : 'chevronDown'" />
    </button>

    <transition
      enter-active-class="transform duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transform duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 top-full z-20 mt-2 flex min-w-[100px] rounded-lg bg-white px-4 py-2 sm:mt-0 sm:w-[180px]"
      >
        <ul
          class="title flex w-full flex-col gap-y-2 overflow-y-auto text-sm uppercase leading-none text-slate"
        >
          <li
            v-for="item in items"
            :key="item.name"
            class="flex w-full cursor-pointer items-center py-2"
            :class="{ 'text-blue': sorting.field === item.name }"
            @click="changeOrder(item.name)"
          >
            <span>
              {{ translations[item.name] }}
            </span>

            <span v-if="sorting.field === item.name" class="ml-2 h-4 w-4">
              <UiIcon container-size icon="chevronDown"></UiIcon>
            </span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useOpenClose, useBodyScrollLock } from '~/composables';

interface Props {
  modelValue?: any;
  items?: any[];
}
interface Emits {
  (e: 'update:modelValue', data: any): void;
}
const { t } = useI18n();
const translations: { [index: string]: any } = {
  'order[best_seller]': t('filter_label_order_best_seller') || 'Featured',
  'order[new]': t('filter_label_order_newest') || 'Newest',
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  items: () => [{ name: 'order[best_seller]' }, { name: 'order[new]' }],
});
const emit = defineEmits<Emits>();

const { isOpen, open, close } = useOpenClose();
useBodyScrollLock(isOpen);
const wrap = ref();
const sorting = reactive<{
  field?: string;
  order?: string;
}>({
  field: undefined,
  order: undefined,
});

const toggleOpenClose = () => {
  isOpen.value ? close() : open();
};

const getSortingOrder = (order: any) => {
  if (order !== sorting.field) return 'desc';
  return undefined;
};

const changeOrder = (field: any) => {
  const order = getSortingOrder(field);

  emit('update:modelValue', { ...(order && { [field]: order }) });
  close();
};

onClickOutside(wrap, () => close());

watch(
  () => props.modelValue,
  (value) => {
    let field;
    let order;

    if (value) [field, order] = Object.entries(value)[0] ?? [];

    sorting.field = field;
    sorting.order = order as any;
  },
  { immediate: true },
);
</script>
